/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module NotificationsModule
 */

import { pluck } from 'underscore';
import { Collection } from 'ajs/modules/data-model/factories/collection.factory';
import { ControllerFault } from 'items/controller-fault.item.factory';

export interface IFaultItem {
    type?: string,
    message?: string,
    details?: string[],
    eventHref?: string,
    actionLabel?: string,
    onActionClick?: () => void,
}

/**
 * @description Collection of Controller Faults.
 * @author Alex Tseung, Alex Malitsky, Aravindh Nagarajan, Umesh Chovatiya
 */
export class ControllerFaultCollection extends Collection {
    public static ngDependencies = [ControllerFault];

    constructor(args = {}) {
        const extendedArgs = {
            objectName: 'controller-inventory',
            allDataSources: {
                list: {
                    source: 'ControllerFaultCollDataSource',
                    transformer: 'ControllerFaultDataTransformer',
                    transport: 'ListDataTransport',
                    fields: ['config', 'faults'],
                },
            },
            defaultDataFields: ['config', 'faults'],
            isStatic: false,
            ...args,
        };

        super(extendedArgs);

        this.itemClass_ = this.getNgDependency(ControllerFault);
    }

    /**
     * Return a list of all fault descriptions.
     * @return Array of fault descriptions.
     */
    public get faultDescriptions(): IFaultItem[] {
        const controllerFaults = this.itemList as ControllerFault[];

        return controllerFaults.reduce((faultsList: IFaultItem[], item: ControllerFault) => {
            const { type, faults: itemFaults } = item.getConfig();

            const descriptions = pluck(itemFaults, 'description');
            const details = pluck(itemFaults, 'details');
            const eventHrefs = pluck(itemFaults, 'events-href');

            const flatFaultItems = this.convertFlatFaultItems(type, descriptions, details, eventHrefs);

            faultsList.push(...flatFaultItems);

            return faultsList;
        }, []);
    }

    /**
     * Convert descriptions and results array to IFaultItem object array.
     */
    private convertFlatFaultItems(
        type: string,
        descriptions: string[],
        details: string[][],
        eventHrefs: string[],
    ): IFaultItem[] {
        let index = 0;

        return descriptions.reduce((faultItems: IFaultItem[], message: string) => {
            const faultObj = {
                type,
                message,
                details: details[index],
                eventHref: eventHrefs[index++],
            };

            faultItems.push(faultObj);

            return faultItems;
        }, []);
    }
}

export type TControllerFaultCollection = typeof ControllerFaultCollection;
